import { useEffect, useState } from "react"

// Intro animation shared by the dashboard charts: every bar launches from its
// baseline, overshoots its value slightly and settles back down.
//
// The intro is held back until the chart is actually scrolled into view, so a
// chart sitting below the fold still animates for the user instead of playing
// to an empty screen. This lives here rather than in any one page's styles, so
// every page rendering these charts gets the same behaviour.

export const BAR_STAGGER = 70 // ms between two neighbouring groups
export const SERIES_STAGGER = 25 // ms between bars inside the same group
export const BAR_GROW_MS = 3000 // ms for a bar to reach its value

// The overshoot in this curve is what makes the bars read as a fountain jet
// rather than a bar that simply slides up.
export const FOUNTAIN_EASING = "cubic-bezier(0.34, 1.45, 0.64, 1)"

// How much of the chart has to be on screen before the bars launch.
const VISIBLE_THRESHOLD = 0.25

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

// Reports the first time the element is meaningfully on screen, then stops
// watching — the intro is a one-off, not a scroll effect that replays.
//
// Takes the element itself rather than a ref so that a chart which mounts late
// (behind a loading skeleton, for instance) still gets observed when it finally
// appears.
function useInView(node) {
  const [inView, setInView] = useState(false)

  useEffect(() => {
    if (inView || !node) return

    // Without IntersectionObserver there is nothing to wait for; animating on
    // mount is a better fallback than never animating at all.
    if (typeof IntersectionObserver !== "function") {
      setInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: VISIBLE_THRESHOLD },
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [node, inView])

  return inView
}

// Runs the intro once, the first time the chart both has data and is visible.
//
// `ready` — flip to true when the data has arrived (labels.length > 0)
// `count` — number of bars, used to know when the last one has landed
//
// Returns:
//   ref     — attach to the chart's root element so visibility can be watched
//   node    — that element once it is mounted, for callers that need to measure
//   growing — false while the bars are parked at zero, true from the frame the
//             intro starts, which is what drives the CSS transition
//   delayFor(groupIndex, seriesIndex) — per-bar stagger in ms, 0 once the
//             intro is over so later data updates respond immediately
export function useBarIntro(ready, count = 0) {
  // A callback ref (rather than a ref object) so the observer effect re-runs
  // when the element mounts, which may be long after this hook first ran.
  const [node, setNode] = useState(null)
  const inView = useInView(node)

  const [phase, setPhase] = useState("idle") // idle -> growing -> done

  useEffect(() => {
    if (!ready || !inView || phase !== "idle") return

    // Nothing should fly across the screen for someone who asked for less
    // motion: the bars just appear at their values.
    if (prefersReducedMotion()) {
      setPhase("done")
      return
    }

    // The effect already runs after the flat frame has been painted; handing
    // the browser one more frame guarantees it has something to transition
    // from. requestAnimationFrame is paused in a hidden tab though, and a bar
    // stuck at zero is a blank chart rather than a missed animation, so a
    // timer races it and whichever lands first releases the bars.
    let released = false
    const release = () => {
      if (released) return
      released = true
      setPhase("growing")
    }

    const raf = requestAnimationFrame(release)
    const fallback = setTimeout(release, 150)

    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(fallback)
    }
  }, [ready, inView, phase])

  useEffect(() => {
    if (phase !== "growing") return

    // Once the last bar has landed the stagger is dropped, so a later data
    // change (a filter, a new period) redraws without a rippling delay.
    const lastDeparture = Math.max(count - 1, 0) * BAR_STAGGER
    const timer = setTimeout(
      () => setPhase("done"),
      lastDeparture + BAR_GROW_MS + 200,
    )

    return () => clearTimeout(timer)
  }, [phase, count])

  const delayFor = (groupIndex = 0, seriesIndex = 0) =>
    phase === "growing"
      ? groupIndex * BAR_STAGGER + seriesIndex * SERIES_STAGGER
      : 0

  return {
    ref: setNode,
    node,
    growing: phase !== "idle",
    delayFor,
  }
}
